import { createContext, useEffect, useState, useCallback } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import getAuthHeaders from "../utils/getAuthHeaders";

export const CartContext = createContext();

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? `${process.env.REACT_APP_NETWORK}/api/cart`
    : "/api/cart";

export function CartProvider({ children }) {
  const { user } = useAuthContext();
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [fulfillment, setFulfillment] = useState(() => {
    try {
      const stored = sessionStorage.getItem("fulfillment");
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Error loading fulfillment from sessionStorage:", error);
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch cart from server
  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart([]);
      setCartTotal(0);
      setItemCount(0);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const authHeader = await getAuthHeaders(user);

      const response = await fetch(API_BASE_URL, {
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.statusText}`);
      }

      const data = await response.json();
      setCart(data.cart || []);
      setCartTotal(data.cartTotal || 0);
      setItemCount(data.itemCount || 0);
      setError(null);
    } catch (err) {
      console.error("Error fetching cart:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch cart when user logs in
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Persist fulfillment to sessionStorage whenever it changes
  useEffect(() => {
    try {
      if (fulfillment) {
        sessionStorage.setItem("fulfillment", JSON.stringify(fulfillment));
      } else {
        sessionStorage.removeItem("fulfillment");
      }
    } catch (error) {
      console.error("Error saving fulfillment to sessionStorage:", error);
    }
  }, [fulfillment]);

  // Add item to cart (or increment quantity if exists)
  const addItem = useCallback(
    async (item) => {
      if (!user) return;

      try {
        const authHeader = await getAuthHeaders(user);

        const response = await fetch(`${API_BASE_URL}/add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          body: JSON.stringify({ item }),
        });

        if (!response.ok) {
          throw new Error(`Failed to add item: ${response.statusText}`);
        }

        const data = await response.json();
        setCart(data.cart);
        setCartTotal(data.cartTotal);
        setItemCount(data.itemCount);
        setError(null);
      } catch (err) {
        console.error("Error adding item:", err);
        setError(err.message);
      }
    },
    [user]
  );

  // Remove item from cart (or decrement quantity)
  const removeItem = useCallback(
    async (item) => {
      if (!user) return;

      try {
        const authHeader = await getAuthHeaders(user);

        // Find current quantity
        const existingItem = cart.find((x) => x.productId === item.productId);
        if (!existingItem) return;

        if (existingItem.quantity === 1) {
          // Delete the item completely
          const response = await fetch(
            `${API_BASE_URL}/remove/${item.productId}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                ...authHeader,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to remove item: ${response.statusText}`);
          }

          const data = await response.json();
          setCart(data.cart);
          setCartTotal(data.cartTotal);
          setItemCount(data.itemCount);
        } else {
          // Decrement quantity
          const response = await fetch(
            `${API_BASE_URL}/update/${item.productId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                ...authHeader,
              },
              body: JSON.stringify({ quantity: existingItem.quantity - 1 }),
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to update item: ${response.statusText}`);
          }

          const data = await response.json();
          setCart(data.cart);
          setCartTotal(data.cartTotal);
          setItemCount(data.itemCount);
        }
        setError(null);
      } catch (err) {
        console.error("Error removing item:", err);
        setError(err.message);
      }
    },
    [user, cart]
  );

  // Update item quantity directly
  const updateItemQuantity = useCallback(
    async (productId, quantity) => {
      if (!user) return;

      try {
        const authHeader = await getAuthHeaders(user);

        const response = await fetch(`${API_BASE_URL}/update/${productId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          body: JSON.stringify({ quantity }),
        });

        if (!response.ok) {
          throw new Error(`Failed to update quantity: ${response.statusText}`);
        }

        const data = await response.json();
        setCart(data.cart);
        setCartTotal(data.cartTotal);
        setItemCount(data.itemCount);
        setError(null);
      } catch (err) {
        console.error("Error updating quantity:", err);
        setError(err.message);
      }
    },
    [user]
  );

  // Clear entire cart
  const clearCart = useCallback(async () => {
    if (!user) return;

    try {
      const authHeader = await getAuthHeaders(user);

      const response = await fetch(`${API_BASE_URL}/clear`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to clear cart: ${response.statusText}`);
      }

      const data = await response.json();
      setCart(data.cart);
      setCartTotal(data.cartTotal);
      setItemCount(data.itemCount);
      setFulfillment(null);
      setError(null);
    } catch (err) {
      console.error("Error clearing cart:", err);
      setError(err.message);
    }
  }, [user]);

  // Set fulfillment info (local state, not server-side)
  const setFulfillmentInfo = useCallback((info) => {
    setFulfillment(info);
  }, []);

  // Legacy support: updateCart function for backward compatibility
  const updateCart = useCallback(
    (action, item) => {
      switch (action) {
        case "ADD_ITEM":
          addItem(item);
          break;
        case "REMOVE_ITEM":
          removeItem(item);
          break;
        case "EMPTY_CART":
          clearCart();
          break;
        case "FULFILLMENT_INFO":
          setFulfillmentInfo(item);
          break;
        case "SYNC_CART":
          // For SYNC_CART, we'll rely on server as source of truth
          // This is handled automatically when user logs in
          console.warn("SYNC_CART is deprecated - cart syncs automatically");
          break;
        default:
          console.warn(`Unknown cart action: ${action}`);
      }
    },
    [addItem, removeItem, clearCart, setFulfillmentInfo]
  );

  const value = {
    cart,
    cartTotal,
    itemCount,
    fulfillment,
    isLoading,
    error,
    // New API functions
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart,
    setFulfillmentInfo,
    refetchCart: fetchCart, // Expose refetch function
    // Legacy support
    updateCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
